import { useEffect, useState } from "react";
import { ShieldCheck, Loader2, RefreshCw, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api";
import { GlassCard, Chip } from "./ui";

const STATUS_TONE = {
  approved: "green", active: "green", success: "green",
  pending: "amber", in_review: "amber", in_audit: "amber", open: "amber",
  rejected: "red", failed: "red",
};
const tone = (s) => STATUS_TONE[(s || "").toLowerCase()] || "slate";

export const KYBVerification = () => {
  const [form, setForm] = useState({ name: "", ein: "", address_line1: "", city: "", state: "", postal_code: "" });
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);

  const load = () => api.get("/kyb/verifications").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error("Please provide consent to run a verification."); return; }
    if (!form.name.trim()) { toast.error("Business legal name is required."); return; }
    setBusy(true);
    try {
      await api.post("/kyb/verify", {
        name: form.name.trim(),
        ein: form.ein || null,
        address: {
          address_line1: form.address_line1, city: form.city,
          state: form.state, postal_code: form.postal_code, country: "US",
        },
        consent: true,
      });
      toast.success("Verification submitted");
      setForm({ name: "", ein: "", address_line1: "", city: "", state: "", postal_code: "" });
      setConsent(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally { setBusy(false); }
  };

  const refresh = async (id) => {
    try { await api.get(`/kyb/verifications/${id}/refresh`); toast.success("Status refreshed"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not refresh"); }
  };

  return (
    <GlassCard className="p-6" data-testid="kyb-card">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck size={18} className="text-blue-300" />
        <h3 className="cc-heading text-lg font-500 text-white">Business Verification (KYB)</h3>
      </div>
      <p className="text-sm text-slate-400 mb-5">Verify your business against Secretary of State records, EIN/TIN, and watchlists.</p>

      <form onSubmit={submit} className="space-y-3" data-testid="kyb-form">
        <input value={form.name} onChange={set("name")} placeholder="Legal business name"
          className="w-full rounded-xl cc-surface-2 border border-white/10 px-4 py-2.5 text-sm text-white placeholder-slate-500"
          data-testid="kyb-name" />
        <input value={form.ein} onChange={set("ein")} placeholder="EIN (e.g. 12-3456789)"
          className="w-full rounded-xl cc-surface-2 border border-white/10 px-4 py-2.5 text-sm text-white placeholder-slate-500"
          data-testid="kyb-ein" />
        <input value={form.address_line1} onChange={set("address_line1")} placeholder="Address line 1"
          className="w-full rounded-xl cc-surface-2 border border-white/10 px-4 py-2.5 text-sm text-white placeholder-slate-500"
          data-testid="kyb-address" />
        <div className="grid grid-cols-3 gap-2">
          <input value={form.city} onChange={set("city")} placeholder="City"
            className="rounded-xl cc-surface-2 border border-white/10 px-3 py-2.5 text-sm text-white placeholder-slate-500" data-testid="kyb-city" />
          <input value={form.state} onChange={set("state")} placeholder="ST" maxLength={2}
            className="rounded-xl cc-surface-2 border border-white/10 px-3 py-2.5 text-sm text-white placeholder-slate-500 uppercase" data-testid="kyb-state" />
          <input value={form.postal_code} onChange={set("postal_code")} placeholder="ZIP"
            className="rounded-xl cc-surface-2 border border-white/10 px-3 py-2.5 text-sm text-white placeholder-slate-500" data-testid="kyb-zip" />
        </div>
        <label className="flex items-start gap-2 text-xs text-slate-400 cursor-pointer" data-testid="kyb-consent-label">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5 accent-blue-500" data-testid="kyb-consent" />
          <span>I authorize ArcaCentum to verify this business's registration, EIN/TIN, and watchlist status using a third-party verification provider, and I have authority to make this request.</span>
        </label>
        <button type="submit" disabled={busy || !consent}
          className="w-full rounded-xl cc-btn-primary text-white py-2.5 text-sm font-500 disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="kyb-submit">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />}
          {busy ? "Verifying…" : "Verify business"}
        </button>
      </form>

      {items.length > 0 && (
        <div className="mt-5 space-y-2" data-testid="kyb-results">
          {items.map((it) => (
            <div key={it.id || it.business_id} className="rounded-lg cc-surface border border-white/[0.05] px-4 py-3" data-testid="kyb-result-row">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <Building2 size={14} className="text-slate-400 shrink-0" />
                  <span className="text-sm text-white truncate">{it.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Chip tone={tone(it.status)}>{it.status || "—"}</Chip>
                  {it.business_id && (
                    <button onClick={() => refresh(it.business_id)} className="text-slate-400 hover:text-white" title="Refresh status" data-testid="kyb-refresh">
                      <RefreshCw size={13} />
                    </button>
                  )}
                </div>
              </div>
              {it.summary && (
                <div className="mt-2 flex flex-wrap gap-2 text-xs">
                  {it.summary.sos && <Chip tone={tone(it.summary.sos)}>SOS: {it.summary.sos}</Chip>}
                  {it.summary.tin && <Chip tone={tone(it.summary.tin)}>EIN/TIN: {it.summary.tin}</Chip>}
                  {it.summary.watchlist && <Chip tone={tone(it.summary.watchlist)}>Watchlist: {it.summary.watchlist}</Chip>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <p className="text-[11px] text-slate-500 mt-4">Business verification only. This is not a consumer credit pull and does not affect any credit score.</p>
    </GlassCard>
  );
};
